import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Fab,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  BabyChangingStation,
  Delete,
  Edit,
  Search,
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AuthState } from "store/features/auth";
import { RootState } from "store";
import { isNurseryAdmin } from "utils/roles";
import { getNurserys } from "apis";
import Axios from "axios";
import { useSnackbar } from "notistack";
import { stringAvatar } from "utils";
import { toAssetUrl } from "utils/apiConfig";
import DeleteUserDialog from "components/DeleteStaffDialog";
import TablePaginationLocalizationProvider from "components/utils/TablePaginationLocalizationProvider";

type Staff = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  role: string;
  classroomId: number;
  classroom: string;
  adminEmail: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    country: string;
    postcode: string;
  };
  photo?: string | File;
  dateOfBirth?: string;
};

type SortKey = "name" | "email" | "role";
type SortDir = "asc" | "desc";

const NurseryPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { role } = useSelector<RootState, AuthState>((store) => store.auth);
  const canMutate = isNurseryAdmin(role);

  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [deleteItem, setDeleteItem] = useState<Staff>();
  const [openDelete, setOpenDelete] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    getNurserys()
      .then((data) => {
        const val =
          data?.assignedNurseries?.map((s: any) => ({
            ...s,
            adminEmail: data?.adminEmail,
          })) || [];
        setStaff(val);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return staff;
    const term = search.toLowerCase();
    return staff.filter(
      (s) =>
        s.firstName?.toLowerCase().includes(term) ||
        s.lastName?.toLowerCase().includes(term) ||
        s.email?.toLowerCase().includes(term),
    );
  }, [staff, search]);

  const sorted = useMemo(() => {
    const list = filtered.slice();
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      switch (sortKey) {
        case "name":
          return (
            dir *
            `${a.firstName} ${a.lastName}`.localeCompare(
              `${b.firstName} ${b.lastName}`,
            )
          );
        case "email":
          return dir * (a.email || "").localeCompare(b.email || "");
        case "role":
          return dir * (a.role || "").localeCompare(b.role || "");
        default:
          return 0;
      }
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const formatAddress = (addr: Staff["address"]) => {
    if (!addr) return "—";
    return (
      [addr.line1, addr.city, addr.postcode, addr.country]
        .filter(Boolean)
        .join(", ") || "—"
    );
  };

  const handleDeleteConfirm = useCallback(() => {
    setOpenDelete(false);
    fetchData();
  }, [fetchData]);

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
          flexWrap: "wrap",
          py: 1.5,
        }}
      >
        <BabyChangingStation sx={{ color: "primary.main", fontSize: 24 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("staff.label")}
        </Typography>
        <Chip
          label={staff.length}
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.text.primary, 0.06),
            fontWeight: 600,
          }}
        />
        <Box
          sx={{ ml: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: "auto" } }}
        >
          <TextField
            size="small"
            placeholder={t("placeholders.select") + "..."}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2, fontSize: 14 },
            }}
            sx={{ width: { xs: "100%", sm: 220 } }}
          />
        </Box>
      </Toolbar>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Container sx={{ py: 2 }}>
          {sorted.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={8}
              color="text.secondary"
            >
              <BabyChangingStation
                sx={{ fontSize: 64, mb: 2, color: "action.disabled" }}
              />
              <Typography variant="h6" gutterBottom>
                {search ? t("attendance.no_match") : t("nursery.empty")}
              </Typography>
              {!search && canMutate && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate("/nursery/add")}
                  sx={{ mt: 2, textTransform: "none", fontWeight: 600 }}
                >
                  {t("nursery.add")}
                </Button>
              )}
            </Box>
          ) : (
            <Card
              variant="outlined"
              sx={{ borderRadius: 3, overflow: "hidden" }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "name"}
                          direction={sortKey === "name" ? sortDir : "asc"}
                          onClick={() => handleSort("name")}
                        >
                          {t("labels.name")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "email"}
                          direction={sortKey === "email" ? sortDir : "asc"}
                          onClick={() => handleSort("email")}
                        >
                          {t("inputs.email")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("inputs.mobile")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "role"}
                          direction={sortKey === "role" ? sortDir : "asc"}
                          onClick={() => handleSort("role")}
                        >
                          {t("inputs.role")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("labels.address")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">
                        {t("labels.action")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sorted
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((member) => (
                        <TableRow key={member.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Avatar
                                src={toAssetUrl(
                                  member.photo as string | undefined,
                                )}
                                {...(!member.photo && member.firstName
                                  ? stringAvatar(
                                      `${member.firstName} ${member.lastName}`,
                                      { width: 32, height: 32, fontSize: 13 },
                                    )
                                  : {
                                      sx: {
                                        width: 32,
                                        height: 32,
                                        fontSize: 13,
                                      },
                                    })}
                              />
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {member.firstName} {member.lastName}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {member.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {member.mobile || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                member.role === "NURSERY_ADMIN"
                                  ? t("labels.admin")
                                  : member.role === "ROOM_LEADER"
                                    ? t("labels.room_leader")
                                    : member.role === "NURSERY_PRACTITIONER"
                                      ? t("labels.nursery_practitioner")
                                      : t("labels.nurse")
                              }
                              color={
                                member.role === "NURSERY_ADMIN"
                                  ? "error"
                                  : "primary"
                              }
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 180 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              title={formatAddress(member.address)}
                            >
                              {formatAddress(member.address)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {canMutate ? (
                              <>
                                <Tooltip title={t("labels.edit")}>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      navigate("/nursery/edit", {
                                        state: member,
                                      })
                                    }
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("buttons.delete")}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setDeleteItem(member);
                                      setOpenDelete(true);
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider />
              <TablePaginationLocalizationProvider>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={sorted.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </TablePaginationLocalizationProvider>
            </Card>
          )}
        </Container>
      )}

      {canMutate && (
        <Fab
          variant="extended"
          color="primary"
          sx={{
            position: "fixed",
            right: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
            pl: 2.5,
            pr: 3,
            gap: 1,
          }}
          onClick={() => navigate("/nursery/add")}
        >
          <Add />
          {t("nursery.add")}
        </Fab>
      )}

      {openDelete && deleteItem && (
        <DeleteUserDialog
          open={openDelete}
          onClose={handleDeleteConfirm}
          item={deleteItem}
        />
      )}
    </>
  );
};

export default NurseryPage;
